import { companyRequest } from '@/company/api';

export type CustomMcpTransport = 'auto' | 'streamable_http' | 'sse' | 'builtin';
export type CustomMcpAuthType =
  | 'none'
  | 'bearer'
  | 'headers'
  | 'basic'
  | 'oauth'
  | 'shopify_dev_app';
export type CustomMcpAudience = 'private' | 'company';
export type CustomMcpStatus = 'connected' | 'needs_authorization' | 'unavailable';

export type CustomMcpTool = {
  name: string;
  title?: string;
  description?: string;
  effect?: 'read' | 'write';
};
export type CustomMcpConnection = {
  id: string;
  name: string;
  url: string;
  transport: CustomMcpTransport;
  authType: CustomMcpAuthType;
  audience: CustomMcpAudience;
  status: CustomMcpStatus;
  toolCount: number;
  canManage: boolean;
  tools?: CustomMcpTool[];
  managedProvider?: string;
  /** The connection's own read grant (the connection-reads projection), when it has one. */
  readAccess?: import('../connection-read-access/api').ConnectionReadItem;
};

export type CustomMcpList = {
  items: CustomMcpConnection[];
  canConnect: boolean;
  canConnectCompany: boolean;
};

const root = '/api/v1/company/custom-mcp';

export const customMcpApi = {
  list: (fetchImpl?: typeof fetch) =>
    companyRequest<CustomMcpList>(root, 'GET', undefined, fetchImpl),
  create: (
    input: {
      name: string;
      url: string;
      transport: CustomMcpTransport;
      auth: {
        type: CustomMcpAuthType;
        token?: string;
        headers?: Record<string, string>;
        username?: string;
        password?: string;
        clientId?: string;
        clientSecret?: string;
        clientIssuer?: string;
      };
      audience: CustomMcpAudience;
    },
    fetchImpl?: typeof fetch,
  ) =>
    companyRequest<{ connection: CustomMcpConnection; authorizationUrl?: string }>(
      root,
      'POST',
      input,
      fetchImpl,
    ),
  /** A merchant's own Shopify Dev App; Poneva runs its MCP server. */
  createShopifyDevApp: (
    input: {
      name?: string;
      shop: string;
      clientId: string;
      clientSecret: string;
      audience: CustomMcpAudience;
    },
    fetchImpl?: typeof fetch,
  ) =>
    companyRequest<{ connection: CustomMcpConnection }>(
      `${root}/shopify-dev-app`,
      'POST',
      input,
      fetchImpl,
    ),
  test: (id: string, fetchImpl?: typeof fetch) =>
    companyRequest<{ connection: CustomMcpConnection }>(
      `${root}/${encodeURIComponent(id)}/test`,
      'POST',
      {},
      fetchImpl,
    ),
  authorize: (id: string, fetchImpl?: typeof fetch) =>
    companyRequest<{ connection: CustomMcpConnection; authorizationUrl?: string }>(
      `${root}/${encodeURIComponent(id)}/authorize`,
      'POST',
      {},
      fetchImpl,
    ),
  remove: (id: string, fetchImpl?: typeof fetch) =>
    companyRequest(`${root}/${encodeURIComponent(id)}`, 'DELETE', undefined, fetchImpl),
};
